import React, { useEffect, useState } from "react";
import { Sparkles, Database, MessageSquare, Shield, Lock } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls =
  "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const DEFAULTS = {
  response_style: "balanced",
  personality: "friendly",
  show_salary_staff: true,
  show_salary_bands_manager: true,
  show_attendance_in_greeting: true,
  history_ttl_hours: 8,
  anonymise_names_to_llm: true,
};

export default function KiraSettingsPage() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/kira/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d === "object") setForm((f) => ({ ...f, ...d }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/kira/settings`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      setMsg({ type: "success", text: "KIRA settings saved." });
    } catch (e) {
      setMsg({ type: "error", text: typeof e.message === "string" ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={`${card} p-5`}><div className="h-48 rounded-lg bg-white/5 animate-pulse" /></div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-[#00ff90]">KIRA Settings</h1>
        <p className="text-white/40 text-sm mt-0.5">Tune the assistant's behaviour for your organisation</p>
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Sparkles size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Model & Voice</span>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Response style</label>
            <select className={inputCls} value={form.response_style}
              onChange={(e) => set("response_style", e.target.value)}>
              <option value="concise">Concise — shorter, to-the-point</option>
              <option value="balanced">Balanced — default</option>
              <option value="detailed">Detailed — longer, more thorough</option>
            </select>
            <p className="text-xs text-white/25 mt-1">Maps to KIRA's per-reply token budget.</p>
          </div>
          <div>
            <label className={labelCls}>Personality</label>
            <select className={inputCls} value={form.personality}
              onChange={(e) => set("personality", e.target.value)}>
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="direct">Direct</option>
            </select>
          </div>
        </div>
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Database size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Data Access</span>
        </div>
        <div className="p-5 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.show_salary_staff}
              onChange={(e) => set("show_salary_staff", e.target.checked)}
              className="w-4 h-4 rounded accent-[#00ff90]" />
            <div>
              <p className="text-sm text-white/70">Staff can see their own salary details in KIRA</p>
              <p className="text-xs text-white/25">Toggles personal payslip + CTC exposure in chat.</p>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.show_salary_bands_manager}
              onChange={(e) => set("show_salary_bands_manager", e.target.checked)}
              className="w-4 h-4 rounded accent-[#00ff90]" />
            <div>
              <p className="text-sm text-white/70">Managers see salary bands for direct reports</p>
              <p className="text-xs text-white/25">Bands only — never exact figures.</p>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.show_attendance_in_greeting}
              onChange={(e) => set("show_attendance_in_greeting", e.target.checked)}
              className="w-4 h-4 rounded accent-[#00ff90]" />
            <div>
              <p className="text-sm text-white/70">Surface attendance hints in greetings</p>
              <p className="text-xs text-white/25">e.g. "good morning, you checked in at 08:59".</p>
            </div>
          </label>
        </div>
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <MessageSquare size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Conversation</span>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>History retention (hours)</label>
            <select className={inputCls}
              value={String(form.history_ttl_hours)}
              onChange={(e) => set("history_ttl_hours", Number(e.target.value))}>
              <option value="2">2 hours</option>
              <option value="8">8 hours</option>
              <option value="24">24 hours</option>
            </select>
            <p className="text-xs text-white/25 mt-1">
              Older Redis chat turns are dropped on the next message.
            </p>
          </div>
        </div>
      </div>

      <div className={`${card} opacity-90`}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Shield size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Privacy</span>
        </div>
        <div className="p-5 flex items-center gap-3">
          <Lock size={12} className="text-yellow-300" />
          <div>
            <p className="text-sm text-white/70">Name anonymisation to Claude — always on</p>
            <p className="text-xs text-white/25">
              Employee names are swapped for role tokens ([Team_Member], [Manager], …) before any
              prompt reaches Anthropic, and restored to first names in the reply. This is enforced
              at the orchestrator chokepoint and cannot be disabled.
            </p>
          </div>
          <span className="ml-auto text-[10px] font-medium uppercase tracking-widest text-yellow-300/80 bg-yellow-400/15 border border-yellow-400/40 rounded-full px-2 py-1">
            Locked on
          </span>
        </div>
      </div>

      {msg && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${
          msg.type === "error"
            ? "border-red-500/30 bg-red-500/10 text-red-300"
            : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"
        }`}>{msg.text}</div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
          className="text-xs px-6 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
          {saving ? "Saving…" : "Save KIRA Settings"}
        </button>
      </div>
    </div>
  );
}
